import React, { useEffect, useState } from 'react'
import { useTranslate, useRecordContext } from 'react-admin'
import { TableRow, TableCell, Typography, Box } from '@mui/material'
import { getCustomVarsFromLiquidationRule } from 'src/components/pages/sales/reservation/helper/getCustomVarsFromLiquidationRule'
import { ShowElement } from 'src/components/uiElements/CRUD/ShowElement'
import { isCustomVarHidden } from 'src/components/pages/sales/reservation/helper/isDisableEditLiquidationButton'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { ASSISTANCE, CAR, HOTEL } from './products'

interface CustomVar {
  value: number
  description: string
  show: boolean
  show_sitio: boolean
  created: boolean
  name: string
}

interface ResultVars {
  id: number
  item_id: number
  cost: number
  final_price_front: number
}

interface Rules {
  customVars: CustomVar[]
  resultVars: ResultVars
}

interface Liquidation {
  taxes_amount: number | null
  comision: number | null
  currency_by_work_unit: any // The actual type is not provided in the input data.
  fare_amount: number | null
  iva: number | null
  currency_by_business_unit: any // The actual type is not provided in the input data.
  over_amount: number | null
  expenses: number | null
  currency_to_show: any // The actual type is not provided in the input data.
  commission_percentage: number | null
  net_agency: number | null
  additional_fare: number | null
  net_price: number
  liquidation_supplier: Liquidation | null
  id: number
  currency_origin: string
  final_price: number
  liquidation_retail: Liquidation | null
  item_id: number
  commission_paid: number | null
  markup: number | null
  liquidation_wholesaler: Liquidation | null
  fee: number
  rules: Rules
  sale_discount: number | null
  taxes_1: number | null
  taxes_2 : number | null
  taxes_3 : number | null
}

interface RequestRule {
  token_workunit: string
  token_product: string
  rules_parameters: {
    product_type: string
    checkin: string
    destination_type: number
    item_id: number
    rule_type: string
    rule_liquidation_type: string
  }
  data: object | null // The actual type is not provided in the input data.
}

interface LiquidationBase {
  message: string
  messsageArg: string
  liquidation: Liquidation
  requestRule: RequestRule
}

interface LiquidationSupplier extends LiquidationBase {}
interface LiquidationRetail extends LiquidationBase {}
interface LiquidationWholesaler extends LiquidationBase {}

interface Pricing {
  taxes_amount: number | null
  comision: number | null
  currency_by_work_unit: object | null
  fare_amount: number | null
  iva: number | null
  currency_by_business_unit: object | null
  over_amount: number | null
  expenses: number | null
  currency_to_show: object | null
  commission_percentage: number | null
  net_agency: number | null
  additional_fare: number | null
  net_price: number
  liquidation_supplier: LiquidationSupplier | null
  id: number
  currency_origin: string
  currency: string
  final_price: number
  liquidation_retail: LiquidationRetail | null
  item_id: number
  commission_paid: number | null
  markup: number | null
  liquidation_wholesaler: LiquidationWholesaler | null
  fee: number
  sale_discount: number | null
  taxes_1: number | null
  taxes_2: number | null
  taxes_3: number | null
}

interface OptionsExchange {
  id: string
  key: string
  value: number
  symbol: string | null | undefined
}

interface WorkUnit {
  description: string
  billing_default_base: any | null
  client_id: number
  billing_alternate_base: any
  parent_id: number
  url: string | null
  is_active: boolean
  auto_billable: boolean
  id: number
  reservation_numbering_id: number
  hash_work_unit: string
  client_numbering_id: number
  token: string
  config: any
  short_name: string
  logo: any
  long_name: string
  logo_voucher: any
  landing_relation: any[]
  client_relation: any
  children: any[]
  currency_relation: any[]
  banner_relation: any[]
}

interface LiquidationPricingProps {
  setIndividualRecord: React.Dispatch<React.SetStateAction<object>>
  node_liquidation: string
  work_unit_relation: WorkUnit
  historical_record:object
}

const LIQUIDATION_TYPE_MAP: Record<string, string> = {
  liquidation_wholesaler: 'WHOLESALER',
  liquidation_retail: 'RETAIL',
  liquidation_supplier: 'SUPPLIER',
};

const validObject = (object: object | null) => {
  if (object && typeof object == 'object' && Object.keys(object).length > 0) {
    return true
  }
  return false
}

const getOptionsExchangeType = (pricing: Pricing[]): OptionsExchange[] => {
  if (validObject(pricing?.[0])) {
    const currency_to_show = pricing[0]?.currency_to_show
    const currency_origin = pricing[0]?.currency ? pricing[0]?.currency : pricing[0]?.currency_origin

    if (validObject(currency_to_show)) {
      const list_currency_to_show = Object.entries(currency_to_show!)
        .filter(([key, value]) => key !== currency_origin)
        .map(([key, value], index) => ({ id: key, key, value, symbol: null }))
      list_currency_to_show.unshift({ id: currency_origin, key: currency_origin, value: 1, symbol: null })
      return list_currency_to_show
    }
  }
  return []
}

export const LiquidationPricing: React.FC<LiquidationPricingProps> = ({
  setIndividualRecord,
  node_liquidation,
  work_unit_relation,
  historical_record = null
}) => {
  const translate = useTranslate()
  const default_record = useRecordContext()

  const [record, setRecord] = useState<object>(default_record)
  const [exchangType, setExchangeType] = useState('')
  const [listCurrencyToShow, setListCurrencyToShow] = useState<OptionsExchange[]>([])
  const [messageError, setMessageError] = useState<any | null>(null)
  const [customsVarsState, setCustomsVarsState] = useState<any[]>([])
  const [customsVarsDefaulValue, setCustomsVarsDefaulValue] = useState<any[]>([])
  const [monetarySymbol, setmonetarySymbol] = useState('')

  useEffect(()=>{
    if(historical_record){
      setRecord({...record,pricing_relation: [historical_record]})
    } else {
      setRecord(default_record)
    }
  },[historical_record])
  
  useEffect(() => {
    if (record) {
      setIndividualRecord && setIndividualRecord(record)
    }
  }, [record, setIndividualRecord])

  const isTrainProduct = (
    node_liquidation: string,
    record: any,
    list_currency_to_show_with_symbol: any | null = null,
    customsVars: any | null = null,
  ) => {
    // ONLY WHEN IS liquidation supplier IN TRAIN SEGMENT AND PASSES, MUST BE CHANGED CURRENTLY CURRENCY TO EUR
    const PASSES = 'train_detail_passes'
    const SEGMENTS = 'train_detail_segment'
    const PRODUCT = record?.product?.ref_table_detail
    const isTrain = PRODUCT === PASSES || PRODUCT === SEGMENTS

    if (node_liquidation === 'liquidation_supplier' && isTrain) {
      setExchangeType('EUR')
      calculateExchangeType('EUR', list_currency_to_show_with_symbol, customsVars)
    }
  }

  const isOtherProduct = (
    node_liquidation: string,
    record: any,
    list_currency_to_show_with_symbol: any | null = null,
    customsVars: any | null = null,
  ) => {

    const PRODUCT = record?.product?.ref_table_detail

    if (node_liquidation === 'liquidation_supplier' && PRODUCT === 'other_detail') {
      const curr = record.pricing_relation[0].currency_origin
      setExchangeType(curr)
      calculateExchangeType(curr, list_currency_to_show_with_symbol, customsVars)
    }
  }

  const isTije = (
    node_liquidation: string,
    record: any,
    list_currency_to_show_with_symbol: any | null = null,
    customsVars: any | null = null,
    work_unit_relation: any | null = null
  ) => {
    // is a reservation with UT "Tije.com"
    // ONLY WHEN IS  TRAIN ASSISTANCE, HOTEL AND CARS, MUST BE CHANGED CURRENTLY CURRENCY TO USD
    const PRODUCT = record?.product?.ref_table_detail
    const isCorrectProduct = PRODUCT === HOTEL || PRODUCT === CAR || PRODUCT === ASSISTANCE
    const tijeWorkUnitHash = 'pcqbdeate-56ugn25va-4i8j4znfy-mocejit'
    const isTijeWorkUnit = tijeWorkUnitHash == work_unit_relation?.hash_work_unit
    const currency = 'USD'
    if (node_liquidation === 'liquidation_supplier' && isCorrectProduct && isTijeWorkUnit) {
      setExchangeType(currency)
      calculateExchangeType(currency, list_currency_to_show_with_symbol, customsVars)
    } 
  }

  const exchangeTypeProcess = async (record: any) => {
    if (record) {
      let liquidation = record?.pricing_relation?.[0]?.[node_liquidation]?.liquidation
      let customsVars = getCustomVarsFromLiquidationRule(liquidation)
      let list_currency_to_show = getOptionsExchangeType(record?.pricing_relation)
      let list_currency_to_show_with_symbol = await addSymbolInCurrencies(list_currency_to_show)
      
      setListCurrencyToShow(list_currency_to_show_with_symbol)
      if (customsVars) {
        setMessageError({})
        setCustomsVarsState(customsVars)
        setCustomsVarsDefaulValue(customsVars)
      }
      if (!customsVars) {
        let message = record?.pricing_relation?.[0]?.[node_liquidation]?.message
        let messsageArg = record?.pricing_relation?.[0]?.[node_liquidation]?.messsageArg
        if (message && messsageArg) {
          setMessageError({ message: message, messsageArg: messsageArg })
        } else if(historical_record){
          setMessageError({message:''})
        }
      }

      const pricing = record?.pricing_relation?.[0]
      if (pricing) {
        const monetarySymbol = pricing?.currency ? pricing?.currency : pricing?.currency_origin
        let listCurrencyToShowVar = listCurrencyToShow

        if (listCurrencyToShow.length === 0) {
          if (
            list_currency_to_show_with_symbol &&
            Array.isArray(list_currency_to_show_with_symbol) &&
            list_currency_to_show_with_symbol.length > 0
          ) {
            listCurrencyToShowVar = list_currency_to_show_with_symbol
          }
        }

        const exchange_type = listCurrencyToShowVar.find((exchange) => exchange.id === monetarySymbol)
        setmonetarySymbol(exchange_type?.symbol ?? monetarySymbol)
        setExchangeType(monetarySymbol)

        isTrainProduct(
          node_liquidation,
          record,
          list_currency_to_show_with_symbol,
          customsVars,
        )

        isOtherProduct(
          node_liquidation,
          record,
          list_currency_to_show_with_symbol,
          customsVars,
        )

        isTije(
          node_liquidation,
          record,
          list_currency_to_show_with_symbol,
          customsVars,
          work_unit_relation
        )
      }
    }
  }

  useEffect(() => {
    exchangeTypeProcess(record)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  const calculateExchangeType = (
    exchange_type_name: string,
    list_currency_to_show_with_symbol: any | null = null,
    customsVars: any | null = null
  ) => {
    // exchange_type_name ex : USD, EUR
    let listCurrencyToShowVar = listCurrencyToShow

    if (listCurrencyToShow.length == 0) {
      if (
        list_currency_to_show_with_symbol &&
        Array.isArray(list_currency_to_show_with_symbol) &&
        list_currency_to_show_with_symbol.length > 0
      ) {
        listCurrencyToShowVar = list_currency_to_show_with_symbol
      }
    }

    const exchange_type = listCurrencyToShowVar.find((exchange) => exchange.id === exchange_type_name)

    let customsVarsDefaulValueVar = customsVarsDefaulValue

    if (customsVarsDefaulValue.length == 0) {
      if (customsVars && Array.isArray(customsVars) && customsVars.length > 0) {
        customsVarsDefaulValueVar = customsVars
      }
    }

    if (exchange_type) {
      setmonetarySymbol(exchange_type?.symbol ? exchange_type?.symbol : '')
      const new_customsVarsState = customsVarsDefaulValueVar.map((vars: any) => {
        return {
          ...vars,
          value: vars?.name?.includes('%') ? Number(vars.value.toFixed(5)) : Number((vars.value * exchange_type.value).toFixed(5)),
        }
      })
      setCustomsVarsState(new_customsVarsState)
    }
  }

  const handleChange = (event: SelectChangeEvent) => {
    setExchangeType(event.target.value)
    calculateExchangeType(event.target.value)
  }

  const addSymbolInCurrencies = async (list_currency_to_show: OptionsExchange[]) => {
    const list_names = list_currency_to_show.map((item) => `short_name=${item.key}`).join('&')
    const params = `/?${list_names}&operator=or_equal`
    const currencies = await currencyServices.getByParams(null, params)
    const list_currency_to_show_with_symbols = list_currency_to_show.map((curency) => {
      const currencySymbolFind = currencies.find(
        (currencySymbol) => currencySymbol.short_name === curency.key
      )
      return { ...curency, symbol: currencySymbolFind?.symbol }
    })
    return list_currency_to_show_with_symbols
  }

  if (messageError && Object.keys(messageError).length > 0 && node_liquidation !== 'liquidation_retail') {
    // decidimos no mostrar el error si es minorista (ya que en algunos flujos no se aplica esta regla), en cambio no se mostrara nada, solo la tarjeta vacia
    return (
      <Typography padding={'20px'} style={{ color: 'red' }}>
        {translate(messageError?.message, { rule: messageError.messsageArg })}
      </Typography>
    )
  }

  if (Array.isArray(customsVarsState) && customsVarsState.length == 0) {
    return null
  }

  const hasFirstTwoDecimalsZero = (value:number) => {
    const decimals = value.toString().split('.')[1] || '';
    return decimals.padEnd(2, '0').slice(0, 2) === '00';
  };

  const hasToShowItem = (item: any): boolean => {
    const isVisible = item.show && item.value !== null && item.value !== 0;
    if (!isVisible) return false;

    const rules: string[] = item.rule_liquidation_type;
    if (!rules?.length) return true;

    const requiredType = LIQUIDATION_TYPE_MAP[node_liquidation];
    return !!requiredType && rules.includes(requiredType);
  };

  return (
    <>
      {isCustomVarHidden(customsVarsState) ? (
        <Typography padding={'20px'}>{translate('ra.notification.liquidation_rule_invalid')}</Typography>
      ) : (
        <>
          <Box>
            <FormControl sx={{ m: 1, minWidth: 120, width: '95%' }} size='small'>
              <Select
                value={exchangType}
                onChange={handleChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
              >
                {listCurrencyToShow.map((option, index) =>
                  index === 0 ? (
                    <MenuItem key={index} value={option.id}>
                      <em>{option.key} </em>
                    </MenuItem>
                  ) : (
                    <MenuItem key={index} value={option.id}>
                      {option.key}{' '}
                      {typeof option.value === 'number'
                        ? `(${numberFormatterWithDecimalAndThousand(option.value)})`
                        : null}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {customsVarsState.map(
            (item, index) => (
              hasToShowItem(item) && (
                <TableRow key={index}>
                  <TableCell>{item?.name}</TableCell>
                  <TableCell>
                    <ShowElement
                      data={hasFirstTwoDecimalsZero(item?.value) ? item.value.toString()?.split('.')[0] :numberFormatterWithDecimalAndThousand(item.value)} 
                      onlyValue
                      blueText
                      monetarySymbol={item.name.includes('%') ? undefined : monetarySymbol}
                      styleValue={{ fontSize: '16px' }}
                      source={undefined}
                      label={undefined}
                      date={undefined}
                      dateTime={undefined}
                    />
                  </TableCell>
                </TableRow>
              )
            )
          )}
        </>
      )}
    </>
  )
}
